import React, { useState, useEffect } from 'react';
import { getDefaultModel, setModelPreference, getModels } from '../services/modelService';
import { useTheme } from '../contexts/ThemeContext';
import { Spinner } from '../components/common/Spinner';

const ModelSettings: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [models, setModels] = useState<any[]>([]);
  const [defaultModels, setDefaultModels] = useState<Record<string, string>>({
    persona: '',
    session: '',
    agent: '',
    system: '',
    team: ''
  });
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const { darkMode } = useTheme();
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all models
        const modelsData = await getModels();
        setModels(modelsData);
        
        // Fetch default models for each entity type
        const entityTypes = ['persona', 'session', 'agent', 'system', 'team'];
        const defaults: Record<string, string> = {};
        
        for (const type of entityTypes) {
          try {
            const defaultData = await getDefaultModel(type, 'default');
            defaults[type] = defaultData.model?.id || '';
          } catch (err) {
            console.error(`Error fetching default for ${type}:`, err);
            defaults[type] = '';
          }
        }
        
        setDefaultModels(defaults);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load model settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleModelChange = async (entityType: string, modelId: string) => {
    setSaving({ ...saving, [entityType]: true });
    
    try {
      await setModelPreference(entityType, 'default', modelId, true);
      setDefaultModels({ ...defaultModels, [entityType]: modelId });
      setError(null);
    } catch (err: any) {
      setError(`Failed to set default model for ${entityType}: ${err.message}`);
    } finally {
      setSaving({ ...saving, [entityType]: false });
    }
  };
  
  if (loading) return <div className="flex justify-center p-8"><Spinner size="lg" /></div>;
  
  return (
    <div className={`w-full ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <h1 className="text-2xl font-bold mb-6">Model Settings</h1>
      
      {error && (
        <div className={`p-4 mb-6 rounded-lg ${
          darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
        }`}>
          {error}
        </div>
      )}
      
      <div className="grid gap-6">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h2 className="text-xl font-semibold mb-4">Default Models</h2>
          <p className="mb-4">
            Set the default models to use for different entity types. These settings will apply
            system-wide unless overridden for specific entities.
          </p>
          
          <div className="space-y-6">
            {Object.entries(defaultModels).map(([entityType, modelId]) => (
              <div key={entityType} className="border-t pt-4 first:border-0 first:pt-0">
                <h3 className="text-lg font-medium mb-2 capitalize">{entityType}</h3>
                <p className={`mb-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {getEntityDescription(entityType)}
                </p>
                
                <div className="relative">
                  <select
                    value={modelId}
                    onChange={(e) => handleModelChange(entityType, e.target.value)}
                    disabled={saving[entityType]}
                    className={`w-full p-2 pr-10 rounded border ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select a default model</option>
                    {models.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.name} ({model.provider}) - ${model.prompt_cost_dollars.toFixed(4)}/1K input
                      </option>
                    ))}
                  </select>
                  
                  {saving[entityType] && (
                    <div className="absolute right-3 top-2">
                      <Spinner size="sm" />
                    </div>
                  )}
                </div>
                
                {modelId && (
                  <div className="mt-2 text-sm">
                    <ModelInfoCard 
                      model={models.find(m => m.id === modelId)} 
                      darkMode={darkMode} 
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h2 className="text-xl font-semibold mb-4">Usage Guidelines</h2>
          <div className="space-y-3">
            <p>
              Default models are used when no specific model is selected for an entity.
              You can override these defaults on a per-entity basis.
            </p>
            <p>
              Consider these factors when selecting default models:
            </p>
            <ul className="list-disc list-inside ml-4">
              <li>Cost - Higher capability models typically cost more</li>
              <li>Context length - Longer contexts allow for more history</li>
              <li>Capabilities - Some tasks require specific features like vision or tools</li>
              <li>Performance - More powerful models may provide better results</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModelInfoCard = ({ model, darkMode }: { model: any, darkMode: boolean }) => {
  if (!model) return null;
  
  return (
    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mt-2`}>
      <div className="grid grid-cols-2 gap-2">
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Context:</div>
        <div>{model.context_length.toLocaleString()} tokens</div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Input Cost:</div>
        <div>${model.prompt_cost_dollars.toFixed(4)}/1K tokens</div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Output Cost:</div>
        <div>${model.completion_cost_dollars.toFixed(4)}/1K tokens</div>
      </div>
      
      <div className="mt-2">
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Capabilities:</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(model.capabilities).map(([key, value]) => (
            value && (
              <span 
                key={key}
                className={`text-xs px-2 py-0.5 rounded ${
                  darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {key}
              </span>
            )
          ))}
        </div>
      </div>
    </div>
  );
};

function getEntityDescription(entityType: string): string {
  switch (entityType) {
    case 'persona':
      return 'The default model used when creating new personas or when a persona doesn\'t have a specific model assigned.';
    case 'session':
      return 'The default model used for new sessions when no specific model is selected.';
    case 'agent':
      return 'The default model used by agents when no specific model is assigned to them.';
    case 'system':
      return 'The fallback model used by the system when no other defaults are available.';
    case 'team':
      return 'The default model used for team operations when no specific model is selected.';
    default:
      return '';
  }
}

export default ModelSettings;
